import { TranslateService } from '@ngx-translate/core';
import { ChartDataset } from '@components/svg-chart';
import { WeatherIndicatorManager } from './weather-indicator.manager';
import { WeatherNormDataManager } from './weather-norm-data.manager';

/**
 * Manager for multi-indicator chart data processing
 * Handles datasets for multiple indicators with optional normalized data
 */
export class MultiIndicatorChartDataManager {
  constructor(
    private translateService: TranslateService,
    private indicatorManager: WeatherIndicatorManager,
    private normDataManager: WeatherNormDataManager
  ) {}

  /**
   * Create datasets for multiple indicators with optional normalized data
   * Each indicator gets its own Y-axis
   * @param indicators - Array of selected indicator keys
   * @param dataByIndicator - Map of indicator key to values array
   * @param normDataByIndicator - Optional map of indicator key to normalized values
   * @returns Array of ChartDatasets
   */
  createMultiIndicatorDatasets(
    indicators: string[],
    dataByIndicator: Map<string, (number | null)[]>,
    normDataByIndicator?: Map<string, (number | null)[]>
  ): ChartDataset[] {
    const datasets: ChartDataset[] = [];

    // Create datasets for each selected indicator
    indicators.forEach((indicator, index) => {
      const values = dataByIndicator.get(indicator) || [];

      const dataset: ChartDataset = {
        label: this.translateService.instant(indicator),
        data: values,
        borderColor: this.indicatorManager.getColor(indicator),
        backgroundColor: this.indicatorManager.getColor(indicator) + '33',
        borderWidth: 2,
        showArea: false,
        yAxisID: index === 0 ? 'y' : 'y1'
      };

      datasets.push(dataset);

      // Add normalized data for this indicator if available
      if (normDataByIndicator) {
        const normValues = normDataByIndicator.get(indicator);
        if (normValues && normValues.length > 0) {
          const normDataset = this.normDataManager.createNormDataset(
            normValues,
            indicator,
            this.translateService.instant(indicator),
            this.indicatorManager.getColor(indicator)
          );

          // Assign same Y-axis as the data it normalizes
          normDataset.yAxisID = index === 0 ? 'y' : 'y1';
          datasets.push(normDataset);
        }
      }
    });

    return datasets;
  }

  /**
   * Create multi-year datasets for multiple indicators
   * @param indicators - Array of selected indicator keys
   * @param yearValues - Array of arrays, each containing values for all months for a specific year
   * @param years - Array of year numbers
   * @param normDataByIndicator - Optional map of indicator key to normalized values
   * @returns Array of ChartDatasets
   */
  createMultiYearMultiIndicatorDatasets(
    indicators: string[],
    yearValues: (number | null)[][],
    years: number[],
    normDataByIndicator?: Map<string, (number | null)[]>
  ): ChartDataset[] {
    const datasets: ChartDataset[] = [];

    // For each indicator, create datasets for each year
    indicators.forEach((indicator, indicatorIndex) => {
      const yAxisID = indicatorIndex === 0 ? 'y' : 'y1';

      // Create dataset for each year
      yearValues.forEach((values, yearIndex) => {
        const year = years[yearIndex];
        const dataset: ChartDataset = {
          label: `${this.translateService.instant(indicator)} (${year})`,
          data: values,
          borderColor: this.indicatorManager.getColor(indicator),
          backgroundColor: this.indicatorManager.getColor(indicator) + '33',
          borderWidth: 2,
          yAxisID
        };

        datasets.push(dataset);
      });

      // Add normalized data for this indicator if available
      if (normDataByIndicator) {
        const normValues = normDataByIndicator.get(indicator);
        if (normValues && normValues.length > 0) {
          const normDataset = this.normDataManager.createNormDataset(
            normValues,
            indicator,
            this.translateService.instant(indicator),
            this.indicatorManager.getColor(indicator)
          );
          normDataset.yAxisID = yAxisID;
          datasets.push(normDataset);
        }
      }
    });

    return datasets;
  }

  /**
   * Create dual-axis configuration for the chart
   * @param indicators - Array of selected indicators
   * @param yAxisData - Values for first Y axis
   * @param y1AxisData - Values for second Y axis
   * @param yAxisUnit - Unit for first Y axis
   * @param y1AxisUnit - Unit for second Y axis
   * @returns Chart scales configuration
   */
  createDualAxisConfig(
    indicators: string[],
    yAxisData: number[],
    y1AxisData: number[],
    yAxisUnit?: string,
    y1AxisUnit?: string
  ): any {
    const yScale = this.calculateAxisScale(yAxisData);
    const y1Scale = this.calculateAxisScale(y1AxisData);

    // Build axis titles with indicator name and unit
    const yAxisTitle = `${this.translateService.instant(indicators[0])}${yAxisUnit ? ' (' + yAxisUnit + ')' : ''}`;
    const y1AxisTitle = indicators[1] ? `${this.translateService.instant(indicators[1])}${y1AxisUnit ? ' (' + y1AxisUnit + ')' : ''}` : '';

    const scales: any = {
      x: {
        stacked: false
      },
      y: {
        stacked: false,
        min: yScale.min,
        max: yScale.max,
        title: {
          display: true,
          text: yAxisTitle
        }
      }
    };

    // Only add y1 axis if there are 2 indicators AND y1 has data
    if (indicators.length > 1 && y1AxisData.length > 0) {
      scales.y1 = {
        stacked: false,
        min: y1Scale.min,
        max: y1Scale.max,
        position: 'right',
        title: {
          display: true,
          text: y1AxisTitle
        }
      };
    }

    return scales;
  }

  /**
   * Calculate scale values for an axis, including normalized data ranges
   * @param values - Array of data values
   * @returns Scale configuration with min, max, and axis values
   */
  private calculateAxisScale(values: number[]): {min: number; max: number; values: number[]} {
    if (values.length === 0) {
      return {min: 0, max: 100, values: [0, 20, 40, 60, 80, 100]};
    }

    let min = Math.min(...values);
    let max = Math.max(...values);

    // Ensure min is 0 if all values are positive
    if (min > 0) min = 0;

    const range = max - min;
    const step = range / 5;

    const isDecimal = max % 1 !== 0;

    const axisValues = Array.from({length: 6}, (_, i) => {
      const value = max - (i * step);
      return isDecimal ? Number(value.toFixed(2)) : Math.round(value);
    });

    return {
      min: Math.min(...axisValues),
      max: Math.max(...axisValues),
      values: axisValues
    };
  }
}
